'use client';
import { Container } from '@/components/primitives/Container/Container';
import { PieChart, Pie, ResponsiveContainer } from 'recharts';
import {
  parseSegments,
  TOTAL_HOURS,
} from '@/components/authorable/shared/PricingChart/PricingBarChart.styles';
import {
  pieChartVariants,
  pieChartConfig,
  pieChartLegendVariants,
  tileSegments,
} from './PricingPieChart.styles';
import { CSLPFieldMapping, IPricingChartModularBlock } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import ModalWrapper from '@/helpers/Wrappers/ModalWrapper/ModalWrapper';
import { useState } from 'react';
import { WithMetadata } from '@/lib/types';

const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
};

const PIE_START_ANGLE = 90;
const PIE_END_ANGLE = -270;

const ClockFace = ({ title = '', titleCslp }: { title?: string; titleCslp?: CSLPFieldMapping }) => {
  const styles = pieChartVariants();
  const { center, radii, anglePerHour, amPmAngles, amPmBox, colors, typography, viewBox } =
    pieChartConfig;
  const { cx, cy } = center;
  const amPos = polarToCartesian(250, 250, radii.amPmLabel, amPmAngles.am);
  const pmPos = polarToCartesian(320, 320, radii.amPmLabel, amPmAngles.pm);

  return (
    <svg
      x={0}
      y={0}
      width="100%"
      height="100%"
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: 'visible' }}
      focusable={false}
      aria-hidden
    >
      {Array.from({ length: TOTAL_HOURS }, (_, h) => {
        const angle = h * anglePerHour;
        const inner = polarToCartesian(cx, cy, radii.tickInner, angle);
        const outer = polarToCartesian(cx, cy, radii.tickOuter, angle);
        return (
          <line
            key={`tick-${h}`}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke={colors.tick}
            strokeWidth={2}
            strokeLinecap="round"
          />
        );
      })}

      {Array.from({ length: TOTAL_HOURS }, (_, h) => {
        const angle = h * anglePerHour;
        const pos = polarToCartesian(cx, cy, radii.label, angle);
        const display = h % 12 === 0 ? 12 : h % 12;
        return (
          <text
            key={`hr-${h}`}
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill={colors.hourLabel}
            fontSize={typography.hourLabel.fontSize}
            fontWeight={typography.hourLabel.fontWeight}
          >
            {display}
          </text>
        );
      })}

      <foreignObject
        x={cx - radii.centerText}
        y={cy - radii.centerText}
        width={radii.centerText * 2}
        height={radii.centerText * 2}
      >
        <div className={styles.centerText()} {...getCSLPAttributes(titleCslp)}>
          {title}
        </div>
      </foreignObject>

      <foreignObject x={amPos.x} y={amPos.y} width={amPmBox.width} height={amPmBox.height}>
        <div className={styles.amPmLabel()}>
          <i
            className="fa-solid fa-sun"
            style={{ color: colors.sun }}
            aria-hidden="true"
            aria-label="AM Label (Sun Icon)"
          />
          <span>AM</span>
        </div>
      </foreignObject>

      <foreignObject
        x={pmPos.x - (amPmBox.width - 30)}
        y={pmPos.y}
        width={amPmBox.width}
        height={amPmBox.height}
      >
        <div className={styles.amPmLabel()}>
          <i
            className="fa-solid fa-moon"
            style={{ color: colors.moon }}
            aria-hidden="true"
            aria-label="PM Label (Moon Icon)"
          />
          <span>PM</span>
        </div>
      </foreignObject>
    </svg>
  );
};

type LegendListProps = {
  items: NonNullable<IPricingChartModularBlock['items']>;
  defaultLegend?: string;
  defaultLegendCslp?: CSLPFieldMapping;
} & Partial<Pick<IPricingChartModularBlock, 'disclaimer'>>;

const LegendList = ({ items, defaultLegend, defaultLegendCslp, disclaimer }: LegendListProps) => {
  const { legendList, legendItem, legendDot, legendContent } = pieChartLegendVariants();

  return (
    <ul className={legendList()}>
      {items.map((item, i) => {
        return (
          <li key={i} className={legendItem()}>
            <span
              className={legendDot()}
              style={{ backgroundColor: item.color?.color_list ?? pieChartConfig.colors.gapFill }}
              aria-hidden="true"
            />
            <RichTextWrapper
              content={item.legend}
              className={legendContent()}
              {...getCSLPAttributes(item.$?.legend)}
            />
          </li>
        );
      })}
      {defaultLegend && (
        <li className={legendItem()}>
          <span
            className={legendDot()}
            style={{ backgroundColor: pieChartConfig.colors.gapFill }}
            aria-hidden="true"
          />
          <RichTextWrapper
            content={defaultLegend}
            className={legendContent()}
            {...getCSLPAttributes(defaultLegendCslp)}
          />
        </li>
      )}
      {disclaimer && (
        <li>
          <Disclaimer {...disclaimer} />
        </li>
      )}
    </ul>
  );
};

const Disclaimer = (disclaimer: IPricingChartModularBlock['disclaimer']) => {
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const { disclaimerLabel, disclaimerIcon } = pieChartLegendVariants();
  if (!disclaimer) return null;

  const { disclaimer_title, disclaimer_content, $ } = disclaimer;

  return (
    <>
      <em className={disclaimerLabel()} {...getCSLPAttributes($?.disclaimer_title)}>
        {disclaimer_title}
        <i
          className={disclaimerIcon()}
          role="button"
          tabIndex={0}
          aria-label={`View ${disclaimer_title ?? 'disclaimer'} details`}
          onClick={() => setIsDisclaimerModalOpen(true)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsDisclaimerModalOpen(true)}
        />
      </em>
      <ModalWrapper
        isOpen={isDisclaimerModalOpen}
        onClose={() => setIsDisclaimerModalOpen(false)}
        title=""
      >
        <RichTextWrapper
          content={disclaimer_content}
          {...getCSLPAttributes($?.disclaimer_content)}
        />
      </ModalWrapper>
    </>
  );
};

export const PricingPieChart = (props: WithMetadata<IPricingChartModularBlock>) => {
  const styles = pieChartVariants();
  const segments = parseSegments(props.items);

  if (!segments.length) return null;

  const tiles = tileSegments(segments);

  return (
    <Container
      componentName="PricingBarChart"
      metaId={props._metadata?.uid}
      containerBleed={props.use_wide_layout}
    >
      <div className={styles.wrapper()}>
        {/* Visually-hidden label associates the chart title with the accessible legend below */}
        {props.title && (
          <span className="sr-only" role="img" aria-label={`Pie Chart of ${props.title}`} />
        )}
        {/* aria-hidden + inert removes the visual chart from the tab order and accessibility tree; data is conveyed via the legend */}
        <div className={styles.chartArea()} aria-hidden="true" inert>
          <ResponsiveContainer
            width="100%"
            height="100%"
            maxHeight={360}
            initialDimension={{ width: 750, height: 380 }}
          >
            <PieChart
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              title={`Pie Chart of ${props.title}`}
              accessibilityLayer={false}
            >
              <Pie
                data={tiles}
                dataKey="duration"
                cx="50%"
                cy="50%"
                innerRadius={pieChartConfig.rechartsRadius.inner}
                outerRadius={pieChartConfig.rechartsRadius.outer}
                startAngle={PIE_START_ANGLE}
                endAngle={PIE_END_ANGLE}
                isAnimationActive={true}
                animationEasing="ease-in"
                stroke="none"
              />
              <ClockFace title={props.title} titleCslp={props.$?.title} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {props.items?.length && (
          <LegendList
            items={props.items}
            defaultLegend={props.default_legend}
            defaultLegendCslp={props.$?.default_legend}
            disclaimer={props.disclaimer}
          />
        )}
      </div>
    </Container>
  );
};
